import React, { useState, ChangeEvent, useEffect, useRef } from "react";

import {
  AccountType,
  DateType,
  useAccount,
} from "@shared/contexts";

import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";
import { FormSection } from "@components/ui";
import { FormFieldGroup } from "@components/ui";

import AccountService from "@features/createaccount/service/AccountService";

import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { PagePaths } from "@shared/constants";
import { SimpleSelect } from "@components/ui";
import {
  cleanFormDataErrors,
  set_localStorage,
} from "../../../shared/utils";
import { getSessionFormValue } from "@shared/utils/storage/sessionFormStorage";
import {
  RegisterPersonalInformationRequest,
  StartTransactionRequest,
} from "../service/AccountRequest";
import { useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_birthdayGender } from "@i18n/usualTexts/t_birthdayGender";
import i18n from "@i18n/index";
import { delay, intervalMiliseconds } from "@shared/utils";
import { useAuthError } from "@shared/hooks/useAuthError";
import { dateFormat } from "@features/createaccount/service/DateService";

export interface FormData {
  birthday: {
    value: DateType;
    errors: Set<string>;
  };
  genderOption: {
    value: string;
    errors: Set<string>;
  };
  gender: {
    value: string;
    errors: Set<string>;
  };
  pronoun: {
    value: string;
    errors: Set<string>;
  };
}

const getLocalStorageValue = (key: string): string => {
  return getSessionFormValue(key);
};

const resolveField = (contextValue: string | undefined, storageKey: string): string => {
  return contextValue ?? getLocalStorageValue(storageKey);
};

const getBirthdayValue = (accountData: AccountType) => {
  const day = resolveField(accountData.birthday?.day, "day");
  const month = resolveField(accountData.birthday?.month, "month");
  const year = resolveField(accountData.birthday?.year, "year");
  const value = resolveField(accountData.birthday?.value, "birthdate");
  return { day, month, year, value };
};

const initializeFormData = (accountData: AccountType): FormData => {
  return {
    birthday: {
      value: getBirthdayValue(accountData),
      errors: new Set<string>(),
    },
    genderOption: {
      value: accountData.genderOption ?? getLocalStorageValue("genderOption"),
      errors: new Set<string>(),
    },
    gender: {
      value: accountData.gender ?? getLocalStorageValue("gender"),
      errors: new Set<string>(),
    },
    pronoun: {
      value: accountData.pronoun ?? getLocalStorageValue("pronoun"),
      errors: new Set<string>(),
    },
  };
};

const BirthdayGenderPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("signup.birthdayGender.pageTitle"));
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { oauth2Data } = useOAuth2();
  const { accountData, setAccountData } = useAccount();
  const { currentToken: antiBotToken, executeRecaptcha, isReady: isRecaptchaReadyCheck } = useRecaptchaContext();
  const { handleRegisterError } = useAuthError();
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(() => initializeFormData(accountData));

  const monthSelectOptions = t(t_birthdayGender.monthPlaceholderSelectOptions).split(",").map(m => m.trim());
  const genderOptions = t(t_birthdayGender.genderPlaceholderSelectOptions).split(",").map(m => m.trim());
  const pronounOptions = t(t_birthdayGender.pronounPlaceholderSelectOptions).split(",").map(m => m.trim());

  // Ensure reCAPTCHA token is available (should already be from previous step, but verify)
  useEffect(() => {
    let isMounted = true;
    let animationFrameId: number | null = null;
    const maxWaitTime = 10000;
    const startTime = Date.now();

    const isReadyNow = (): boolean => !!(isRecaptchaReadyCheck && isRecaptchaReadyCheck());
    const isTimedOut = (): boolean => Date.now() - startTime > maxWaitTime;

    const waitForRecaptchaReady = (): Promise<boolean> => {
      return new Promise((resolve) => {
        const checkReady = () => {
          if (!isMounted) { resolve(false); return; }
          if (isReadyNow()) { resolve(true); return; }
          if (isTimedOut()) { resolve(false); return; }
          animationFrameId = requestAnimationFrame(checkReady);
        };
        checkReady();
      });
    };

    const setReady = () => { if (isMounted) { setIsRecaptchaReady(true); setRecaptchaError(null); } };

    const executeRecaptchaToken = async () => {
      if (!executeRecaptcha) {
        throw new Error(i18n.t('error.runtime.recaptchaUnavailable'));
      }
      const isReady = await waitForRecaptchaReady();
      if (!isReady) {
        throw new Error(i18n.t('error.runtime.recaptchaNotReadyTimeout'));
      }
      const recaptchaResult = await executeRecaptcha();
      if (!recaptchaResult.success) {
        throw new Error(`${i18n.t('error.runtime.recaptchaFailed')}: ${recaptchaResult.error || 'Unknown error'}`);
      }
    };

    const ensureRecaptchaReady = async () => {
      try {
        if (antiBotToken) { setReady(); return; }
        await executeRecaptchaToken();
        setReady();
      } catch (error) {
        if (!isMounted) return;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setRecaptchaError(errorMessage);
        setIsRecaptchaReady(false);
      }
    };

    ensureRecaptchaReady();

    return () => {
      isMounted = false;
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [antiBotToken, executeRecaptcha, isRecaptchaReadyCheck]);

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    if (!(name === "gender"))
      formData.birthday.value[name as keyof DateType] = value;
    else {
      formData[name as keyof FormData].value = value;
    }

    formData.birthday.value.value = dateFormat(formData.birthday.value, monthSelectOptions);

    setFormData({
      ...formData,
    });
    cleanFormDataErrors(formData);
  }

  function handleFormSelect(selectValue: string, selectName: string) {
    if (!(selectName.includes("gender") || selectName === "pronoun"))
      formData.birthday.value[selectName as keyof DateType] = selectValue;
    else {
      formData[selectName as keyof FormData].value = selectValue;
      if (selectValue === genderOptions[2]) {
        formData.gender.value = "";
        formData.pronoun.value = "";
      }
    }

    formData.birthday.value.value = dateFormat(formData.birthday.value, monthSelectOptions);

    setFormData({
      ...formData,
    });
    cleanFormDataErrors(formData);
  }

  function back() {
    setAccountData({
      ...accountData,
      step: 1,
    });
    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.CREATEACCOUNT));
  }

  function validateGenderFields(accountService: AccountService): void {
    if (formData.genderOption.value !== genderOptions[2]) {
      formData.gender.value = formData.genderOption.value;
      formData.pronoun.value = formData.genderOption.value;
      return;
    }

    if (formData.gender.value.trim()) {
      accountService.checkName(
        formData.gender.value,
        "gender",
        t(t_birthdayGender.genderPlaceholderSelect)
      );
    }
    if (formData.pronoun.value.trim()) {
      accountService.checkSelect(
        formData.pronoun.value,
        "pronoun",
        t(t_birthdayGender.pronounPlaceholderSelect),
        pronounOptions
      );
    }
  }

  function hasFormErrors(): boolean {
    return formData.birthday.errors.size > 0 ||
           formData.genderOption.errors.size > 0 ||
           formData.gender.errors.size > 0 ||
           formData.pronoun.errors.size > 0;
  }

  function checkThisFormDatas() {
    const accountService: AccountService = new AccountService(t);
    accountService.checkDate(
      formData.birthday.value,
      "birthday",
      monthSelectOptions
    );
    accountService.checkSelect(
      formData.genderOption.value,
      "genderOption",
      t(t_birthdayGender.genderPlaceholderSelect),
      genderOptions
    );
    validateGenderFields(accountService);
    return !hasFormErrors();
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }

  async function postFormDatas_and_navigate(newAccountData: AccountType) {
    // Ensure reCAPTCHA is ready before proceeding
    if (!isRecaptchaReady) {
      throw new Error(t('error.recaptcha.notReady'));
    }

    // Get the current token (should be available now)
    let tokenToUse = antiBotToken;
    if (!tokenToUse) {
      throw new Error(t('error.recaptcha.tokenNotAvailable'));
    }
    
    const startTransactionPostResponse = await StartTransactionRequest(oauth2Data, tokenToUse);
    console.log(startTransactionPostResponse);

    const intervalMilisecondsValue = intervalMiliseconds(startTransactionPostResponse);
    await delay(intervalMilisecondsValue);

    const personalInfoPostResponse = await RegisterPersonalInformationRequest(
      accountData,
      formData.gender.value,
      formData.pronoun.value,
      formData.birthday.value.value,
      startTransactionPostResponse?.access_token,
      tokenToUse || undefined
    );
    console.log(personalInfoPostResponse);

    const updatedAccountData: AccountType = {
      ...newAccountData,
      personalInfoPostResponse: personalInfoPostResponse,
    };

    setAccountData(updatedAccountData);
    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.INSERTUSERNAME));
  }

  function checkRecaptchaReadiness(): boolean {
    if (isRecaptchaReady) return true;
    setServerErrorPopUp(recaptchaError || t('help.recaptchaWaiting'));
    return false;
  }

  const isSubmittingRef = useRef(false);

  async function handleSubmit() {
    if (isSubmittingRef.current) return;
    if (!checkRecaptchaReadiness()) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    setSubmitted(true);

    try {
      const isValid = checkThisFormDatas();
      if (!isValid) {
        setIsLoading(false);
        isSubmittingRef.current = false;
        return;
      }

      const step = 3;
      const newAccountData: AccountType = {
        ...accountData,
        birthday: formData.birthday.value,
        genderOption: formData.genderOption.value,
        gender: formData.gender.value,
        pronoun: formData.pronoun.value,
        step: step,
      };

      await postFormDatas_and_navigate(newAccountData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
      setIsLoading(false);
      isSubmittingRef.current = false;
      await delay(1000);
      setSubmitted(false);
    } finally {
      set_localStorage(formData);
    }
  }

  const renderBirthdayErrors = () => {
    if (formData.birthday.errors.size === 0) return null;
    
    return (
      <FormSection spacing="sm">
        {[...formData.birthday.errors].map((error) => (
          <div
            key={error}
            data-testid="spanElement"
            className={"text-red-500 text-sm block" + (submitted ? " shake-animation" : "")}
            role="alert"
            aria-live="polite"
          >
            {error}
          </div>
        ))}
      </FormSection>
    );
  };

  const renderCustomGenderFields = () => {
    if (formData.genderOption.value !== genderOptions[2]) return null;
    
    return (
      <>
        <FormField
          name="gender"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.gender.value}
          errors={formData.gender.errors}
          submitted={submitted}
        >
          {t(t_birthdayGender.genderPlaceholder)}
        </FormField>
        <SimpleSelect
          name="pronoun"
          required={true}
          onChange={handleFormSelect}
          value={formData.pronoun.value}
          errors={formData.pronoun.errors}
          submitted={submitted}
          options={pronounOptions}
        >
          {t(t_birthdayGender.pronounPlaceholderSelect)}
        </SimpleSelect>
      </>
    );
  };

  // Show loading state while reCAPTCHA is initializing
  const isFormLoading = isLoading || !isRecaptchaReady;

  return (
    <AuthenticationForm
      title={t(t_birthdayGender.title)}
      subtitle={t(t_birthdayGender.subtitle)}
      back={back}
      onSubmit={() => handleSubmit()}
      isLoading={isFormLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <FormFieldGroup columns={3} spacing="sm">
        <FormField
          autofocus={true}
          name="day"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.birthday.value.day}
          submitted={submitted}
          errors={formData.birthday.errors}
          toPresentErrors={false}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
        >
          {t(t_birthdayGender.dayPlaceholder)}
        </FormField>
        <SimpleSelect
          name="month"
          required={true}
          onChange={handleFormSelect}
          value={formData.birthday.value.month}
          submitted={submitted}
          options={monthSelectOptions}
          errors={formData.birthday.errors}
          toPresentErrors={false}
        >
          {t(t_birthdayGender.monthPlaceholderSelect)}
        </SimpleSelect>
        <FormField
          name="year"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.birthday.value.year}
          submitted={submitted}
          errors={formData.birthday.errors}
          toPresentErrors={false}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
        >
          {t(t_birthdayGender.yearPlaceholder)}
        </FormField>
      </FormFieldGroup>

      {renderBirthdayErrors()}

      <SimpleSelect
        name="genderOption"
        required={true}
        onChange={handleFormSelect}
        value={formData.genderOption.value}
        errors={formData.genderOption.errors}
        submitted={submitted}
        options={genderOptions}
      >
        {t(t_birthdayGender.genderPlaceholderSelect)}
      </SimpleSelect>

      {renderCustomGenderFields()}
    </AuthenticationForm>
  );
};

export default BirthdayGenderPage;
